import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Button, Form, Table } from "react-bootstrap";
import PageTitle from "../../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert2";
import { logout } from "../../../redux/actions/AuthActions";
import { getInsuranceListById, saveAuthorityLetterData, uploadCompanyAuthorityLetter } from "../../../redux/actions/InsuranceActions";
import { getMasterClaimTypeList } from "../../../redux/actions/MasterActions";
import Preloader from "../../../Components/Preloader";
import Select from "react-dropdown-select";
import JoditEditor from "jodit-react";
import "../../../css/App.css";
import apiClient from "../../../axios/apiClient";

const AuthorityLetter = ({ contentType }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedClaimType, setSelectedClaimType] = useState(null);
  const [letterContent, setLetterContent] = useState("");
  const [editorValue, setEditorValue] = useState(""); // Separate state for editor value
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [onlyOfficeScriptLoaded, setOnlyOfficeScriptLoaded] = useState(false);
  const [onlyOfficeError, setOnlyOfficeError] = useState("");
  const [onlyOfficeConfig, setOnlyOfficeConfig] = useState(null);
  const [onlyOfficeInstanceKey, setOnlyOfficeInstanceKey] = useState(0);
  const [showUpdateTemplate, setShowUpdateTemplate] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const onlyOfficeEditorRef = useRef(null);
  const [selectedCompanyDoc, setSelectedCompanyDoc] = useState(null);

  const {
    AuthReducer: { auth, loading },
    insuranceReducer: { insuranceLists, staticData, loadingInsurance, errorsInsurance, changedInsuranceStatus },
    masterReducer: { masterClaimTypeList, loadingMaster },
  } = useSelector((state) => state);

  const isReportFormatMode = contentType === "reportFormat";
  const isRequirementMode = contentType === "requirementLetter";
  const pageLabel = isReportFormatMode ? "Report Format" : isRequirementMode ? "Requirement Letter" : "Authority Letter";
  const pagePath = isReportFormatMode ? `/Report-Format/${id}` : isRequirementMode ? `/Requirement-Letter/${id}` : `/Authority-Letter/${id}`;

  useEffect(() => {
    if (id && auth.token) {
      dispatch(getInsuranceListById(auth.token, id));
    }
  }, [id, auth.token, dispatch]);

  useEffect(() => {
    if (auth.token) {
      dispatch(getMasterClaimTypeList(auth.token));
    }
  }, [auth.token, dispatch]);

  useEffect(() => {
    if (errorsInsurance?.status === 401) {
      dispatch(logout());
      toast.error(t("Unauthorized Access"));
      navigate("/login");
    }
  }, [errorsInsurance, dispatch, navigate, t]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop().toLowerCase();

      setUploadedFile({ name: fileName, file: file });

      if (fileExtension === 'docx') {
        if (!isReportFormatMode) {
          toast.success("DOCX selected. Click Upload Template to push it to S3.");
          return;
        }
        try {
          setLetterContent("");
          setEditorValue("");
        } catch (error) {
          console.error('Error handling Word file:', error);
          toast.error("Error preparing Word file.");
        }
      } else if (fileExtension === 'doc') {
        toast.info("Old Word format (.doc) is not supported. Please convert to .docx or manually type/edit the content.");
        setLetterContent("");
      } else if (fileExtension === 'pdf') {
        toast.info("PDF files are not directly editable. Please manually type/edit the content in the editor below.");
        setLetterContent("");
      } else if (fileExtension === 'txt') {
        const reader = new FileReader();
        reader.onload = (e) => {
          setLetterContent(e.target.result);
          setEditorValue(e.target.result); // Update editor value for external content
          toast.success("Text file content loaded in the editor. You can now edit it.");
        };
        reader.readAsText(file);
      } else {
        toast.info("File uploaded. You can create/edit the letter in the editor below.");
        setLetterContent("");
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAuthorityTemplate = () => {
    if (!selectedClaimType) {
      toast.error("Please select a Type of Claim first.");
      return;
    }
    if (!uploadedFile?.file) {
      toast.error("Please choose a .docx file first.");
      return;
    }

    const lowerName = (uploadedFile.file.name || "").toLowerCase();
    if (!lowerName.endsWith(".docx")) {
      toast.error(`Only .docx files are allowed for ${isReportFormatMode ? "report format" : isRequirementMode ? "requirement letter" : "authority letter"} template.`);
      return;
    }

    const formData = new FormData();
    formData.append("insurance_company_id", String(id));
    formData.append("type_of_claim_id", String(selectedClaimType));
    formData.append("file", uploadedFile.file);
    dispatch(uploadCompanyAuthorityLetter(auth.token, formData, isReportFormatMode ? "reportFormat" : isRequirementMode ? "requirementLetter" : "authorityLetter"));
  };

  const handleSave = () => {
    if (!selectedClaimType) {
      toast.error("Please select a Type of Claim first.");
      return;
    }

    // // Get latest content from Jodit editor
    let contentToSave = letterContent;
    // if (editorRef.current && editorRef.current.editor) {
    //   contentToSave = editorRef.current.editor.value;
    // }

    // if (!contentToSave || contentToSave.trim() === '' || contentToSave === '<p><br></p>') {
    //   toast.error(`Please add some content to the ${contentType === 'reportFormat' ? 'report' : 'authority letter'}.`);
    //   return;
    // }

    const payload = {
      insurance_company_id: id,
      type_of_claim_id: selectedClaimType,
      [contentType]: contentToSave,
    };

    dispatch(saveAuthorityLetterData(auth.token, payload));
  };

  useEffect(() => {
    if (changedInsuranceStatus?.success || changedInsuranceStatus?.status) {
      const message = changedInsuranceStatus.message || `${pageLabel} updated successfully!`;
      toast.success(message);
      swal.fire({
        show: true,
        title: "Great !",
        text: message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
      });

      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh current company document URLs/content
      dispatch(getInsuranceListById(auth.token, id));

      // Force ONLYOFFICE remount/re-init after update (prevents blank editor on replace uploads)
      setOnlyOfficeInstanceKey((k) => k + 1);

      // Close update template controls after update
      setShowUpdateTemplate(false);

      // Reset so the same success doesn't pop on other pages (Index)
      dispatch({ type: "RESET_DELETE_INSURANCE_STATUS" });
    }
    // else if (changedInsuranceStatus && !changedInsuranceStatus.success) {
    //   toast.error(changedInsuranceS  tatus.message || "Error saving authority letter");
    // }
  }, [changedInsuranceStatus, dispatch, auth.token, id, pageLabel]);

  // Load content when insuranceLists data is first loaded and claimType is already selected
  useEffect(() => {
    if (selectedClaimType && insuranceLists) {
      // Check if insuranceLists is an array or single object
      const insuranceData = Array.isArray(insuranceLists) && insuranceLists.length > 0
        ? insuranceLists[0]
        : insuranceLists;

      const insuranceCompanyDetails = insuranceData?.insuranceCompanyDetails;

      if (insuranceCompanyDetails && Array.isArray(insuranceCompanyDetails)) {
        const filteredData = insuranceCompanyDetails.find(
          (item) => Number(item.type_of_claim_id) === Number(selectedClaimType)
        );
        setSelectedCompanyDoc(filteredData || null);

        if (filteredData) {
          // Set content based on contentType prop (authorityLetter or reportFormat)
          const contentToSet = contentType === 'reportFormat'
            ? filteredData.reportFormat
            : contentType === 'requirementLetter'
              ? filteredData.requirementLetter
              : filteredData.authorityLetter;

          if (contentToSet) {
            setLetterContent(contentToSet);
            setEditorValue(contentToSet); // Update editor value for external content
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceLists, selectedClaimType, contentType]);

  const onlyOfficeDocsUrl = (process.env.REACT_APP_ONLYOFFICE_DOCS_URL || "https://docs.asginnovations.in")
    .replace(/\/+$/, "");
  const authorityLetterUrl = selectedCompanyDoc?.authorityLetterUrl || "";
  const reportFormatUrl = selectedCompanyDoc?.reportFormatUrl || "";
  const requirementLetterUrl = selectedCompanyDoc?.requirementLetterUrl || "";
  const activeDocxUrl = isReportFormatMode ? reportFormatUrl : isRequirementMode ? requirementLetterUrl : authorityLetterUrl;

  // IMPORTANT: when replacing an existing DOCX, ONLYOFFICE can go blank if we re-use the same container id.
  // So we generate a fresh container id whenever the doc url changes.
  useEffect(() => {
    if (!activeDocxUrl) return;
    setOnlyOfficeInstanceKey((k) => k + 1);
  }, [activeDocxUrl]);

  const onlyOfficeContainerId = `onlyoffice-authority-editor-${onlyOfficeInstanceKey}`;

  // Keep the UI minimal: collapse update controls on claim/url change
  useEffect(() => {
    setShowUpdateTemplate(false);
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClaimType, activeDocxUrl]);

  useEffect(() => {
    if (!activeDocxUrl) return;
    setOnlyOfficeError("");
    if (window.DocsAPI?.DocEditor) {
      setOnlyOfficeScriptLoaded(true);
      return;
    }
    const scriptId = "onlyoffice-docs-api-script";
    const existingScript = document.getElementById(scriptId);
    if (existingScript) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `${onlyOfficeDocsUrl}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.onload = () => {
      setOnlyOfficeScriptLoaded(true);
      setOnlyOfficeError("");
    };
    script.onerror = () => {
      setOnlyOfficeError("Unable to load ONLYOFFICE script.");
      setOnlyOfficeScriptLoaded(false);
    };
    document.body.appendChild(script);
  }, [activeDocxUrl, onlyOfficeDocsUrl]);

  useEffect(() => {
    if (!activeDocxUrl || !onlyOfficeScriptLoaded || !selectedClaimType) return;
    if (!window.DocsAPI?.DocEditor) return;
    let cancelled = false;
    const containerId = onlyOfficeContainerId;

    const initOnlyOffice = async () => {
      try {
        setOnlyOfficeError("");

        // Wait for the container to exist in DOM (prevents blank editor on fast remounts)
        const waitForContainer = async () => {
          const maxAttempts = 25; // ~2.5s total
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (cancelled) return false;
            if (document.getElementById(containerId)) return true;
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 100));
          }
          return false;
        };

        const ready = await waitForContainer();
        if (!ready || cancelled) return;

        const tryFetchConfig = async (url) => {
          return await apiClient.get(url, {
            params: {
              insurance_company_id: id,
              type_of_claim_id: selectedClaimType,
            },
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          });
        };

        const primaryConfigUrl = isReportFormatMode
          ? "/api/admin/v1/Onlyoffice-Report-Format-Config"
          : isRequirementMode
            ? "/api/admin/v1/Onlyoffice-Requirement-Letter-Config"
            : "/api/admin/v1/Onlyoffice-Authority-Letter-Config";

        let response;
        try {
          response = await tryFetchConfig(primaryConfigUrl);
        } catch (e) {
          // Fallback: if requirement config endpoint isn't available yet, try authority config
          const status = e?.response?.status;
          if (isRequirementMode && (status === 404 || status === 405)) {
            response = await tryFetchConfig("/api/admin/v1/Onlyoffice-Authority-Letter-Config");
          } else {
            throw e;
          }
        }

        const config = response?.data?.data;
        if (!config || cancelled) return;

        setOnlyOfficeConfig(config);

        if (onlyOfficeEditorRef.current?.destroyEditor) {
          onlyOfficeEditorRef.current.destroyEditor();
        }

        onlyOfficeEditorRef.current = new window.DocsAPI.DocEditor(containerId, {
          ...config,
          height: "100%",
          width: "100%",
          events: {
            onError: (event) => {
              const code = event?.data?.errorCode ?? event?.data?.error?.code;
              const description =
                event?.data?.errorDescription ||
                event?.data?.error?.description ||
                event?.data?.message ||
                "ONLYOFFICE editor error.";
              const message = code ? `ONLYOFFICE error ${code}: ${description}` : description;
              console.error("ONLYOFFICE onError payload:", event);
              setOnlyOfficeError(message);
            },
          },
        });
      } catch (error) {
        if (cancelled) return;
        const msg = error?.response?.data?.message || "Unable to load ONLYOFFICE configuration.";
        setOnlyOfficeError(msg);
      }
    };

    initOnlyOffice();

    return () => {
      cancelled = true;
      if (onlyOfficeEditorRef.current?.destroyEditor) {
        onlyOfficeEditorRef.current.destroyEditor();
        onlyOfficeEditorRef.current = null;
      }
    };
  }, [activeDocxUrl, onlyOfficeScriptLoaded, auth.token, id, isReportFormatMode, isRequirementMode, selectedClaimType, onlyOfficeContainerId]);

  // onChange handler - update state directly
  const handleEditorChange = useCallback((newContent) => {
    setLetterContent(newContent);
  }, []);


  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: base.borderColor,
      '&:hover': {
        borderColor: base.borderColor,
      }
    }),
  };

  const handleCopy = (text, event) => {
    // Prevent default to avoid multiple triggers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Copy to clipboard only, no automatic paste
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success("Copied to clipboard");
        })
        .catch(() => {
          toast.error("Failed to copy");
        });
    } else {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        toast.success("Copied to clipboard");
      } catch (e) {
        toast.error("Failed to copy");
      }
    }
  };

  const FIELD_MAPPING = staticData || {};
  const showUploadTemplateOption =
    selectedClaimType &&
    (!selectedCompanyDoc || (isReportFormatMode
      ? (!selectedCompanyDoc.reportFormat && !selectedCompanyDoc.reportFormatUrl)
      : isRequirementMode
        ? (!selectedCompanyDoc.requirementLetter && !selectedCompanyDoc.requirementLetterUrl)
        : (!selectedCompanyDoc.authorityLetter && !selectedCompanyDoc.authorityLetterUrl)));
  const showEditorSection = !showUploadTemplateOption;

  // Jodit Editor Configuration - Memoized to prevent re-renders
  const joditConfig = useMemo(() => ({
    readonly: false,
    height: "calc(297mm - 42mm)",
    placeholder: "Start typing...",
    toolbar: true,
    spellcheck: true,
    language: "en",
    toolbarButtonSize: "medium",
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: false,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: true,
    defaultActionOnPaste: "insert_as_html",
    buttons: [
      "source", "|",
      "bold", "strikethrough", "underline", "italic", "|",
      "superscript", "subscript", "|",
      "ul", "ol", "|",
      "outdent", "indent", "|",
      "font", "fontsize", "brush", "paragraph", "|",
      "image", "link", "table", "|",
      "align", "undo", "redo", "|",
      "hr", "eraser", "copyformat", "|",
      "fullsize", "selectall", "print", "|",
      "symbol", "fullscreen", "preview", "find",
    ],
    removeButtons: [],
    showTooltip: true,
    showTooltipDelay: 0,
    useNativeTooltip: false,
    uploader: {
      insertImageAsBase64URI: true,
      accept: "image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/svg+xml",
    },
    disablePlugins: ["filebrowser"],
    image: {
      editSrc: true,
      upload: true,
    },
    link: {
      openInNewTab: true,
    },
    table: {
      insert: true,
      delete: true,
      addColumn: true,
      deleteColumn: true,
      addRow: true,
      deleteRow: true,
      merge: true,
      split: true,
    },
  }), []);

  if (loading || loadingInsurance) {
    return <Preloader />;
  }

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Insurance Company", path: "/Insurance-Company" },
          { label: pageLabel, path: pagePath, active: true },
        ]}
        title={t(pageLabel)}
      />
      <Row>
        <Col xs={12}>
          <Card>
            <Card.Body>
              <div className="authority-letter-container">

                <Row className="mb-4 align-items-end">
                  <Col lg={3}>
                    <Form.Group>
                      <Form.Label>{t("Type of Claim")} <span style={{ color: 'red', fontSize: 15 }}>*</span></Form.Label>
                      <Select
                        options={masterClaimTypeList}
                        values={selectedClaimType ? masterClaimTypeList?.filter(claim => claim.id === selectedClaimType) : []}
                        name="claimType"
                        labelField={"name"}
                        valueField={"id"}
                        searchBy={"name"}
                        placeholder={t("Select Type of Claim")}
                        loading={loadingMaster}
                        onChange={(selectedData) => {
                          const claimTypeId = selectedData[0]?.id;
                          setSelectedClaimType(claimTypeId);

                          // Filter insuranceCompanyDetails based on claimType_id and set in text editor
                          if (claimTypeId) {
                            // Check if insuranceLists is an array or single object
                            const insuranceData = Array.isArray(insuranceLists) && insuranceLists.length > 0
                              ? insuranceLists[0]
                              : insuranceLists;

                            const insuranceCompanyDetails = insuranceData?.insuranceCompanyDetails;

                            console.log('Claim Type Selected:', claimTypeId);
                            console.log('Insurance Data:', insuranceData);
                            console.log('Insurance Company Details:', insuranceCompanyDetails);
                            console.log('Content Type:', contentType);

                            if (insuranceCompanyDetails && Array.isArray(insuranceCompanyDetails)) {
                              const filteredData = insuranceCompanyDetails.find(
                                (item) => Number(item.type_of_claim_id) === Number(claimTypeId)
                              );
                              setSelectedCompanyDoc(filteredData || null);

                              console.log('Filtered Data:', filteredData);

                              if (filteredData) {
                                // Set content based on contentType prop (authorityLetter or reportFormat)
                                const contentToSet = contentType === 'reportFormat'
                                  ? filteredData.reportFormat
                                  : filteredData.authorityLetter;

                                console.log('Content to Set:', contentToSet ? 'Content found' : 'No content');

                                if (contentToSet) {
                                  setLetterContent(contentToSet);
                                  setEditorValue(contentToSet); // Update editor value for external content
                                  toast.success("Content loaded in editor");
                                } else {
                                  setLetterContent("");
                                  setEditorValue("");
                                  toast.info("No content available for this claim type");
                                }
                              } else {
                                setSelectedCompanyDoc(null);
                                setLetterContent("");
                                setEditorValue("");
                                toast.info("No data found for selected claim type");
                              }
                            } else {
                              setSelectedCompanyDoc(null);
                              setLetterContent("");
                              setEditorValue("");
                              if (!insuranceData) {
                                toast.warning("Insurance data not loaded yet");
                              }
                            }
                          } else {
                            setLetterContent("");
                            setEditorValue("");
                          }
                        }}
                        styles={customSelectStyles}
                      />
                    </Form.Group>
                  </Col>

                  {selectedClaimType && !!activeDocxUrl && (
                    <Col lg={9}>
                      {!showUpdateTemplate && (
                        <div className="d-flex align-items-center justify-content-start">
                          <Button
                            variant="primary"
                            className="p-1"
                            onClick={() => setShowUpdateTemplate(true)}
                          >
                            Update Template
                          </Button>
                        </div>
                      )}

                      {showUpdateTemplate && (
                        <Form.Group className="mt-2">
                          <Form.Label className="mb-1">{t("Upload Template (.docx)")}</Form.Label>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <Form.Control
                              type="file"
                              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              onChange={handleFileChange}
                              ref={fileInputRef}
                              style={{ maxWidth: 520 }}
                            />

                            <Button
                              variant="primary"
                              onClick={handleUploadAuthorityTemplate}
                              disabled={!uploadedFile?.file}
                            >
                              Upload Template
                            </Button>

                            {uploadedFile?.file && (
                              <Button variant="outline-secondary" onClick={handleRemoveFile}>
                                Remove
                              </Button>
                            )}
                          </div>
                        </Form.Group>
                      )}
                    </Col>
                  )}
                </Row>

                {selectedClaimType && (
                  <>
                    {showUploadTemplateOption && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <Card className="border-0 shadow-sm">
                            <Card.Body style={{ background: "#f8fbff", borderRadius: 12 }}>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <Form.Label className="mb-0 fw-semibold">
                                  Upload {pageLabel} Template (.docx)
                                </Form.Label>
                                <span className="badge bg-light text-dark border">Required</span>
                              </div>

                              <div
                                style={{
                                  border: "2px dashed #87b7ff",
                                  borderRadius: 10,
                                  padding: 18,
                                  background: "#ffffff",
                                }}
                              >
                                <div className="d-flex align-items-center mb-2">
                                  <i className="mdi mdi-file-word-outline text-primary me-2" style={{ fontSize: 22 }} />
                                  <strong>Choose a Word file</strong>
                                </div>
                                <Form.Control
                                  type="file"
                                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  onChange={handleFileChange}
                                  ref={fileInputRef}
                                />
                                <div className="mt-2 text-muted">Only `.docx` format is supported</div>
                              </div>

                              {uploadedFile?.file && (
                                <div className="mt-2 small text-success">
                                  Selected: {uploadedFile.file.name}
                                </div>
                              )}

                              <small className="text-muted d-block mt-2">
                                {isReportFormatMode
                                  ? "No report format found for selected claim type. Upload template to continue."
                                  : isRequirementMode
                                    ? "No requirement letter found for selected claim type. Upload template to continue."
                                    : "No authority letter found for selected claim type. Upload template to continue."}
                              </small>

                              <div className="mt-3 d-flex gap-2">
                                <Button
                                  variant="primary"
                                  onClick={handleUploadAuthorityTemplate}
                                  disabled={!uploadedFile?.file}
                                >
                                  Upload Template
                                </Button>
                                {uploadedFile?.file && (
                                  <Button
                                    variant="outline-secondary"
                                    onClick={handleRemoveFile}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {showEditorSection && (
                    <Row className="mb-4">
                      <Col xs={12} lg={10}>
                        <Form.Group>
                          <Form.Label>
                            {t(isReportFormatMode ? "Report Content" : isRequirementMode ? "Requirement Letter Content" : "Authority Letter Content")}
                          </Form.Label>
                          {activeDocxUrl ? (
                            <>
                              
                              <div
                                style={{
                                  border: "1px solid #dee2e6",
                                  borderRadius: 8,
                                  overflow: "hidden",
                                  background: "#fff",
                                  height: "80vh",
                                  minHeight: "700px",
                                }}
                              >
                                <div
                                  key={onlyOfficeContainerId}
                                  id={onlyOfficeContainerId}
                                  style={{ width: "100%", height: "100%" }}
                                />
                              </div>
                            </>
                          ) : (
                            <div
                              style={{
                                marginBottom: "20px",
                                display: "flex",
                                justifyContent: "center",
                                background: "#f1f3f5",
                                padding: "16px 10px",
                                borderRadius: 8,
                                overflowX: "auto",
                              }}
                            >
                              <div
                                style={{
                                  width: "210mm",
                                  minWidth: "210mm",
                                  height: "297mm",
                                  background: "#fff",
                                  boxShadow: "0 1px 6px rgba(0,0,0,0.15)",
                                  padding: "12mm",
                                  boxSizing: "border-box",
                                  overflow: "hidden",
                                }}
                              >
                                <JoditEditor
                                  ref={editorRef}
                                  value={editorValue}
                                  config={joditConfig}
                                  tabIndex={1}
                                  onChange={handleEditorChange}
                                />
                              </div>
                            </div>
                          )}
                        </Form.Group>

                        {!activeDocxUrl && (
                          <Row>
                            <Col xs={12} className="text-center">
                              <Button variant="success" onClick={handleSave}>
                                <i className="mdi mdi-content-save"></i>{" "}
                                {isReportFormatMode ? "Save Report" : isRequirementMode ? "Save Requirement Letter" : "Save Authority Letter"}
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </Col>

                      <Col xs={12} lg={2}>
                          <Card>
                            <Card.Body>
                              <Form.Group className="mb-3">
                                <Form.Control
                                  type="text"
                                  placeholder="Filter fields..."
                                  value={filterText}
                                  onChange={(e) => setFilterText(e.target.value)}
                                  style={{ width: "100%" }}
                                />
                              </Form.Group>
                              <div
                                style={{
                                  width: "180px",
                                  maxHeight: 440, // ~10 rows visible
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                }}
                              >
                                <Table
                                  className="mb-0 compact-table"
                                  style={{ width: "100%", tableLayout: "fixed" }}
                                >
                                  <tbody>
                                    {Object.entries(FIELD_MAPPING)
                                      .filter(([displayKey, valueKey]) => {
                                        const searchTerm = filterText.toLowerCase();
                                        return (
                                          displayKey.toLowerCase().includes(searchTerm) ||
                                          valueKey.toLowerCase().includes(searchTerm) ||
                                          `{{${displayKey.toLowerCase()}}}`.includes(searchTerm) ||
                                          `{{${valueKey.toLowerCase()}}}`.includes(searchTerm)
                                        );
                                      })
                                      .map(([displayKey, valueKey]) => (
                                        <tr key={displayKey} style={{ height: 44 }}>
                                          <td
                                            style={{
                                              verticalAlign: "middle",
                                              padding: "10px 12px",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                            title={`{{${displayKey.toLowerCase()}}}`}
                                          >
                                            {`{{${displayKey.toLowerCase()}}}`}
                                          </td>
                                          <td
                                            onClick={(e) =>
                                              handleCopy(`{{${valueKey.toLowerCase()}}}`, e)
                                            }
                                            style={{
                                              width: 44,
                                              minWidth: 44,
                                              maxWidth: 44,
                                              verticalAlign: "middle",
                                              padding: "10px 12px",
                                              textAlign: "right",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <i className="fe-copy text-primary"></i>
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                    </Row>
                    )}




                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

      </Row>
    </>
  );
};

export default AuthorityLetter;


